import { QUERY_PARAM_TO_LISTING_KEY } from "src/hooks/use-handle-search-params";
import type {
  ListingFacet,
  ListingFacetKeys,
  ListingFacets,
  ListingSortKeys,
  SelectedFacets,
} from "src/types/adapters/listing.type";
import { isListingSortKey } from "src/utils/type-guards";
import { MAP_URL_VALUE_TO_SORT, ORDER_BY_PARAM } from "./constants";

const PRICE_MIN_PARAM = "min_price";
const PRICE_MAX_PARAM = "max_price";

export function loadSortFromUrl(
  searchParams: URLSearchParams
): ListingSortKeys | undefined {
  const orderByParam = searchParams.get(ORDER_BY_PARAM);

  if (!orderByParam) {
    return undefined;
  }

  const sortKey = MAP_URL_VALUE_TO_SORT[orderByParam];

  return isListingSortKey(sortKey) ? sortKey : undefined;
}

export function loadSelectedFacetsFromUrl(
  searchParams: URLSearchParams
): SelectedFacets {
  const result: SelectedFacets = {};

  for (const [paramKey, paramValue] of searchParams.entries()) {
    if (!Object.hasOwn(QUERY_PARAM_TO_LISTING_KEY, paramKey)) {
      continue;
    }

    const listingKey =
      QUERY_PARAM_TO_LISTING_KEY[
        paramKey as keyof typeof QUERY_PARAM_TO_LISTING_KEY
      ];

    if (!listingKey || listingKey === "price") {
      continue;
    }

    const existing = result[listingKey] || [];
    result[listingKey] = [...existing, paramValue];
  }

  const minPrice = searchParams.get(PRICE_MIN_PARAM);
  const maxPrice = searchParams.get(PRICE_MAX_PARAM);

  if (minPrice || maxPrice) {
    result.price = [`${minPrice || ""}-${maxPrice || ""}`];
  }

  return result;
}

function applySelectionToFacet(
  key: ListingFacetKeys,
  facet: ListingFacet,
  appliedValues: string[] | undefined
): ListingFacet {
  if (facet.type === "range") {
    const value = appliedValues?.[0];

    if (!value) {
      const { currentValue: _removed, ...data } = facet.data;
      return { ...facet, data };
    }

    const [minStr, maxStr] = value.split("-");
    const min = Number(minStr);
    const max = Number(maxStr);

    if (Number.isNaN(min) || Number.isNaN(max)) {
      return facet;
    }

    return { ...facet, data: { ...facet.data, currentValue: { min, max } } };
  }

  const selected = new Set(appliedValues || []);
  const markSelected = <T extends { value: string; selected: boolean }>(
    options: T[]
  ): T[] =>
    options.map((option) => ({
      ...option,
      selected: selected.has(option.value),
    }));

  if (facet.type === "color") {
    return { ...facet, data: markSelected(facet.data) };
  }

  if (facet.type === "category") {
    return { ...facet, data: markSelected(facet.data) };
  }

  if (facet.type === "brand") {
    return { ...facet, data: markSelected(facet.data) };
  }

  if (facet.type === "size") {
    return { ...facet, data: markSelected(facet.data) };
  }

  return { ...facet, key, data: markSelected(facet.data) };
}

export function applySelectedFacetsToFacets(
  facets: ListingFacets,
  appliedFacets: SelectedFacets
): ListingFacets {
  const updated: ListingFacets = new Map();

  for (const [key, facet] of facets.entries()) {
    updated.set(key, applySelectionToFacet(key, facet, appliedFacets[key]));
  }

  return updated;
}

function arraysEqualAsSets(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }

  const aSet = new Set(a);
  return b.every((value) => aSet.has(value));
}

export function selectedFacetsEqual(
  a: SelectedFacets,
  b: SelectedFacets
): boolean {
  const keys = new Set<ListingFacetKeys>([
    ...(Object.keys(a) as ListingFacetKeys[]),
    ...(Object.keys(b) as ListingFacetKeys[]),
  ]);

  for (const key of keys) {
    if (!arraysEqualAsSets(a[key] || [], b[key] || [])) {
      return false;
    }
  }

  return true;
}
